import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowSquareOut as ExternalLink } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CaddyErrorCard } from "../components/CaddyErrorCard";
import { CaddySidebar } from "../components/CaddySidebar";
import { CaddyBottomNav } from "../components/CaddyBottomNav";
import { FinisherCertificate } from "../components/FinisherCertificate";
import { FeelTrends } from "../components/FeelTrends";
import { SeasonReel } from "../components/SeasonCard";
import { LockedCard } from "../components/game/LockedCard";
import { RelayLoader } from "../components/RelayLoader";
import { mask, usePrivacy } from '../lib/privacy';

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR = {
  bg: "var(--pr-bg)",
  ink: "var(--pr-ink)",
  ink2: "var(--pr-ink2)",
  muted: "var(--pr-muted)",
  line: "var(--pr-line)",
  card: "var(--pr-card)",
  gold: "var(--pr-gold)",
  red: "var(--pr-red)",
  green: "var(--pr-green)",
  teal: "var(--pr-teal)",
};

const fmt = (n) => (n == null ? "n/a" : mask(`$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`));
const shortDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[m - 1]} ${d}`;
};

// A race spans two calendar years, so the header says which years they are.
const rangeLabel = (startIso, endIso) => {
  if (!startIso || !endIso) return "";
  const [sy] = startIso.split("-").map(Number);
  const [ey] = endIso.split("-").map(Number);
  return `${shortDate(startIso)}, ${sy} to ${shortDate(endIso)}, ${ey}`;
};

const cellStyle = (w) => {
  if (w.status === "pto") {
    return { background: "var(--pr-teal-surface)", color: "#fff", ...(w.in_progress ? { border: `2px solid ${COLOR.gold}` } : {}) };
  }
  if (w.in_progress) return { background: "var(--pr-tint-gold)", border: `2px solid ${COLOR.gold}`, color: COLOR.ink };
  switch (w.status) {
    case "green": return { background: "var(--pr-green-surface)", color: "#fff" };
    case "cleared": return { background: "var(--pr-ink-surface)", color: "#fff" };
    case "near":
    case "red": return { background: "var(--pr-tint-red)", color: COLOR.red };
    case "upcoming": return { background: "var(--pr-bg-alt)", color: COLOR.muted };
    default: return { background: "var(--pr-card)", border: `1px solid ${COLOR.line}`, color: COLOR.muted };
  }
};

const statusLabel = (w) => {
  if (w.status === "pto") return "PTO week 🌴, planned time off, streak safe";
  if (w.in_progress) return "In progress. This is your current relay";
  switch (w.status) {
    case "green": return "In the green";
    case "cleared": return "Relay cleared";
    case "near":
    case "red": return "Ended short";
    case "upcoming": return "Up ahead";
    default: return "No target set for this relay";
  }
};

// Match the responsive column count of the grid below (7 / sm:10 / lg:13) so the
// inline detail panel can be inserted right after the selected relay's row.
const useColumns = () => {
  const read = () => {
    if (typeof window === "undefined") return 7;
    if (window.matchMedia("(min-width: 1024px)").matches) return 13;
    if (window.matchMedia("(min-width: 640px)").matches) return 10;
    return 7;
  };
  const [cols, setCols] = useState(read);
  useEffect(() => {
    const on = () => setCols(read());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return cols;
};

const Stat = ({ label, value, color }) => (
  <div className="rounded-2xl p-5 lg:p-6 pr-card text-center">
    <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: COLOR.muted }}>{label}</p>
    <p className="text-xl font-bold" style={{ fontFamily: "'Fraunces', Georgia, serif", color: color || COLOR.ink }}>{value}</p>
  </div>
);

const GoalCard = ({ title, emoji, d, testid }) => {
  const pct = d.goal > 0 && d.paid != null ? Math.min((d.paid / d.goal) * 100, 100) : null;
  return (
    <div className="rounded-2xl p-5 lg:p-6 pr-card" data-testid={testid}>
      <p className="text-[10px] font-bold uppercase tracking-widest mb-2.5" style={{ color: COLOR.muted }}>{emoji} {title}</p>
      <div className="flex justify-between gap-2 text-xs" style={{ color: COLOR.ink2 }}>
        <span>Goal<br /><b className="text-sm" style={{ color: COLOR.ink }}>{fmt(d.goal)}</b></span>
        <span>Paid<br /><b className="text-sm" style={{ color: COLOR.green }}>{fmt(d.paid)}</b></span>
        <span className="text-right">Remaining<br /><b className="text-sm" style={{ color: COLOR.ink }}>{fmt(d.remaining)}</b></span>
      </div>
      {pct != null && (
        <div className="h-2.5 rounded-full overflow-hidden mt-3" style={{ background: COLOR.line }}>
          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: "var(--pr-gold)" }} />
        </div>
      )}
    </div>
  );
};

const MyProgress = ({ progress, source, banks }) => {
  if (!progress) return null;
  const { debt, savings, pto } = progress;
  if (!debt && !savings && !pto) return null;
  const month = banks?.source === "sheet" ? banks.month : null;
  return (
    <div className="mt-6" data-testid="scoreboard-progress">
      <div className="rounded-xl px-4 py-3 mb-3 pr-card-dark" style={{ background: "var(--pr-ink-surface)" }}>
        <p className="text-sm font-bold tracking-[0.2em]" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.gold }}>
          MY PROGRESS
        </p>
      </div>
      {(debt || savings) && (
        <div className="grid sm:grid-cols-2 gap-3">
          {debt && <GoalCard title="Debt" emoji="💳" d={debt} testid="progress-debt" />}
          {savings && <GoalCard title="Savings" emoji="🎯" d={savings} testid="progress-savings" />}
        </div>
      )}
      {pto && (
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          {pto && (
            <div className="rounded-2xl p-5 lg:p-6 pr-card" data-testid="progress-pto">
              <p className="text-[10px] font-bold uppercase tracking-widest mb-2.5" style={{ color: COLOR.muted }}>🏖️ Paid Time Off</p>
              <div className="flex justify-between gap-2 text-xs" style={{ color: COLOR.ink2 }}>
                <span>Accrued<br /><b className="text-sm" style={{ color: COLOR.green }}>{fmt(pto.accrued)}</b></span>
                <span>Drawn<br /><b className="text-sm" style={{ color: COLOR.ink }}>{fmt(pto.drawn)}</b></span>
                <span className="text-right">Remaining<br /><b className="text-sm" style={{ color: COLOR.ink }}>{fmt(pto.remaining)}</b></span>
              </div>
              {month && (
                <p className="text-[11px] mt-2" style={{ color: COLOR.ink2 }} data-testid="progress-pto-month">
                  {month.name}: <b style={{ color: COLOR.green }}>{fmt(month.pto_accrued)}</b> accrued · <b style={{ color: COLOR.ink }}>{fmt(month.pto_used)}</b> used
                </p>
              )}
            </div>
          )}
        </div>
      )}
      {source !== "sheet" && (
        <p className="text-[11px] mt-2.5" style={{ color: COLOR.muted }} data-testid="progress-source-note">
          Live Paid / Remaining numbers come from your connected sheet's Scoreboard tab.
        </p>
      )}
    </div>
  );
};

export default function ScoreboardPage() {
  usePrivacy();  // re-render when Creator Privacy Mode flips
  const cols = useColumns();
  const [view, setView] = useState("loading"); // loading | signed_out | not_member | ready
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [taxItems, setTaxItems] = useState([]);

  const toggleTax = async (item) => {
    const next = !item.paid;
    setTaxItems((prev) => prev.map((t) => (t.quarter === item.quarter ? { ...t, paid: next } : t)));
    try {
      const res = await fetch(`${API}/api/caddy/tax-paid`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quarter: item.quarter, paid: next }),
      });
      const out = await res.json();
      if (!res.ok) throw new Error(out.detail || "failed");
      toast.success(
        next
          ? `${item.quarter} marked paid ✓${out.synced_to_sheet ? ", ticked in your sheet too" : ""}`
          : `${item.quarter} marked unpaid${out.synced_to_sheet ? ", updated in your sheet" : ""}`
      );
    } catch {
      setTaxItems((prev) => prev.map((t) => (t.quarter === item.quarter ? { ...t, paid: item.paid } : t)));
      toast.error("Couldn't update, try again");
    }
  };

  const [resets, setResets] = useState([]);
  const [bonusOpen, setBonusOpen] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/caddy/scoreboard`, { credentials: "include" });
        if (res.status === 401) { setView("signed_out"); return; }
        if (res.status === 403) { setView("not_member"); return; }
        if (!res.ok) { setView("error"); return; }
        const json = await res.json();
        setData(json);
        setTaxItems(json.tax_checklist?.items || []);
        setView("ready");
        fetch(`${API}/api/caddy/quarterly/history`, { credentials: "include" })
          .then((r) => (r.ok ? r.json() : null))
          .then((q) => setResets(q?.resets || []))
          .catch(() => {});
      } catch {
        setView("error");
      }
    })();
  }, []);

  if (view === "error") return <CaddyErrorCard />;

  if (view === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--pr-bg)" }} data-testid="scoreboard-loading">
        <div className="text-center">
          <RelayLoader label="Loading your scoreboard…" />
        </div>
      </div>
    );
  }

  if (view === "signed_out" || view === "not_member") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--pr-bg)" }}>
        <div className="max-w-sm w-full text-center" data-testid="scoreboard-gate">
          <h1 className="text-3xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
            Yearly Scoreboard
          </h1>
          <p className="text-sm leading-relaxed mb-8" style={{ color: COLOR.ink2 }}>
            {view === "signed_out"
              ? "Sign in to see all 52 relays of your race at a glance."
              : "The Yearly Scoreboard comes with your Payday Relay race, start a Free Sprint to open it."}
          </p>
          <Link
            to={view === "signed_out" ? "/login?next=/app/scoreboard" : "/start/free-sprint"}
            className="inline-block px-8 py-3.5 rounded-full font-bold text-base"
            style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
            data-testid="scoreboard-gate-cta"
          >
            {view === "signed_out" ? "Sign in with email →" : "Start Free Sprint →"}
          </Link>
        </div>
      </div>
    );
  }

  const weeks = data.weeks || [];
  const t = data.totals || {};
  const positiveMonths = (data.bonus_months || []).filter((m) => m.net > 0);
  const isSprint = data.entitlements?.mode === "sprint";
  const sprintWeeks = data.entitlements?.features?.race_board_weeks || 52;

  return (
    <div className="min-h-screen" style={{ background: "var(--pr-bg)" }} data-testid="scoreboard-page">
      <div className="w-full max-w-7xl mx-auto lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-8 lg:px-8 lg:pt-8">
        <CaddySidebar active="scoreboard" sheetUrl={data.sheet_url} sprint={isSprint} />
        <main className="max-w-4xl mx-auto lg:mx-0 px-5 py-6 lg:px-0 lg:pt-0 w-full">
        <div className="flex items-center justify-between mb-6">
          <Link to="/app" className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: COLOR.ink2 }} data-testid="scoreboard-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Payday Relay
          </Link>
          {data.sheet_url && (
            <a
              href={data.sheet_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold underline"
              style={{ color: COLOR.ink }}
              data-testid="scoreboard-sheet-link"
            >
              Open in your sheet <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>

        <div className="rounded-2xl px-6 py-6 mb-6 pr-card-dark" style={{ background: "var(--pr-ink-surface)" }} data-testid="scoreboard-banner">
          <h1 className="text-2xl sm:text-3xl tracking-[0.08em]" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.gold }}>
            SCOREBOARD
          </h1>
          <p className="text-xs mt-1.5" style={{ color: "rgba(255,255,255,0.7)" }} data-testid="scoreboard-range">
            {data.season_start ? `${rangeLabel(data.season_start, data.season_end)} · one square per relay` : "One square per relay"}
          </p>
          <Link to="/app/monthly" className="inline-flex items-center gap-1 mt-3 text-xs font-bold underline" style={{ color: COLOR.gold }} data-testid="scoreboard-monthly-link">
            Monthly Review →
          </Link>
        </div>

        <FeelTrends resets={resets} />

        {weeks.length === 0 ? (
          <div className="rounded-2xl p-5 lg:p-6 pr-card text-center" data-testid="scoreboard-empty">
            <p className="text-sm font-bold mb-1" style={{ color: COLOR.ink }}>Your race hasn&apos;t started yet</p>
            <p className="text-xs mb-4" style={{ color: COLOR.ink2 }}>
              Once your relay calendar is set, all 52 relays show up here, red, black, and green.
            </p>
            <Link to="/app" className="inline-block px-5 py-2 rounded-full text-sm font-bold" style={{ background: "var(--pr-ink-surface)", color: "#fff" }}>
              Back to Payday Relay →
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3" data-testid="scoreboard-totals">
              <Stat label="Relays run" value={t.run ?? 0} />
              <Stat label="Cleared" value={t.cleared ?? 0} color={COLOR.green} />
              <Stat label="Earned this race" value={fmt(t.earned)} />
              {isSprint
                ? <Stat label="Bonus banked 🔒" value="Season Pass" color={COLOR.muted} />
                : (
                  <button
                    type="button"
                    onClick={() => setBonusOpen((v) => !v)}
                    className="rounded-2xl p-5 lg:p-6 pr-card text-left transition-transform active:scale-[0.99]"
                    aria-expanded={bonusOpen}
                    data-testid="bonus-tracker-toggle"
                  >
                    <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: COLOR.muted }}>
                      Bonus banked
                    </p>
                    <p className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.green }}>
                      {fmt(t.surplus)}
                    </p>
                    <p className="text-[11px] mt-1 font-bold" style={{ color: COLOR.ink2 }}>
                      {bonusOpen ? "Hide the months ↑" : "See the months ↓"}
                    </p>
                  </button>
                )}
            </div>

            {!isSprint && bonusOpen && (
              <div className="rounded-2xl p-5 lg:p-6 pr-card mb-6" data-testid="bonus-tracker">
                <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: COLOR.muted }}>
                  Bonus tracker · months that finished ahead
                </p>
                {positiveMonths.length === 0 ? (
                  <p className="text-xs leading-relaxed" style={{ color: COLOR.ink2 }} data-testid="bonus-tracker-empty">
                    No month has finished net positive yet. Once a month ends with more earned than
                    targeted, the difference lands here.
                  </p>
                ) : (
                  <>
                    <ul className="space-y-2">
                      {positiveMonths.map((m) => (
                        <li key={m.month} className="flex items-baseline justify-between gap-3 text-sm border-b pb-2 last:border-0"
                            style={{ borderColor: COLOR.line }} data-testid={`bonus-month-${m.month}`}>
                          <span style={{ color: COLOR.ink }}>
                            <b>{m.label}</b>
                            <span className="block text-[11px]" style={{ color: COLOR.muted }}>
                              {fmt(m.earned)} earned vs {fmt(m.target)} targeted · {m.relays} relay{m.relays === 1 ? "" : "s"}
                            </span>
                          </span>
                          <b style={{ color: COLOR.green }}>+{fmt(m.net)}</b>
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-baseline justify-between gap-3 text-sm pt-3 mt-1">
                      <b style={{ color: COLOR.ink }}>Bonus banked</b>
                      <b style={{ color: COLOR.green }} data-testid="bonus-tracker-total">{fmt(t.surplus)}</b>
                    </div>
                  </>
                )}
                <p className="text-[11px] mt-2.5 leading-relaxed" style={{ color: COLOR.muted }}>
                  Only finished relays count. A light month never subtracts from the bank.
                </p>
              </div>
            )}

            {/* Finisher certificate, race complete, share it like a recap card */}
            {data.season_end && data.today > data.season_end && (
              <div className="mb-6" data-testid="scoreboard-finisher">
                <FinisherCertificate />
              </div>
            )}

            {isSprint && (
              <LockedCard id="scoreboard-sprint-note" icon="🏁" title={`Relays ${sprintWeeks + 1}–52 · the full Race Board`}
                lockLine="Season Pass feature"
                body={`Your Sprint is Relays 1–${sprintWeeks}, live below. The 52-week race adds the full board, month-by-month history, and a year-end scorecard you can share.`}
                ctaLabel="Start My 52-Week Payday Relay Race" compact />
            )}

            {(() => {
              const selectedIndex = selected ? weeks.findIndex((w) => w.relay_number === selected.relay_number) : -1;
              const rowEndIndex = selectedIndex >= 0
                ? Math.min(Math.floor(selectedIndex / cols) * cols + cols - 1, weeks.length - 1)
                : -1;
              const detailPanel = selected ? (
                <div
                  className="col-span-full rounded-2xl p-5 my-1.5 pr-card"
                  style={{ gridColumn: "1 / -1" }}
                  data-testid="scoreboard-detail"
                >
                  <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: COLOR.muted }}>
                    Relay #{selected.relay_number} · {shortDate(selected.week_start)} – {shortDate(selected.week_end)}
                  </p>
                  <p className="text-lg font-bold mb-1" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
                    {statusLabel(selected)}
                  </p>
                  {selected.status !== "upcoming" && (
                    <p className="text-sm" style={{ color: COLOR.ink2 }}>
                      {fmt(selected.earned)} earned{selected.target ? ` of a ${fmt(selected.target)} target` : ""}
                    </p>
                  )}
                  {selected.payout_date && (
                    <p className="text-xs mt-1" style={{ color: COLOR.muted }} data-testid="scoreboard-detail-payout">
                      💸 Payout day: {shortDate(selected.payout_date)}
                    </p>
                  )}
                  {selected.needs_closeout && (
                    <p className="text-xs mt-1 font-bold inline-flex items-center gap-1.5" style={{ color: COLOR.gold }} data-testid="scoreboard-detail-closeout">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: COLOR.gold }} /> Ready to close out
                    </p>
                  )}
                  {selected.status !== "upcoming" && (
                    <div>
                      <Link
                        to={`/app/history?week_of=${selected.week_start}`}
                        className="inline-flex items-center gap-1 mt-3 px-4 py-2 rounded-full text-xs font-bold"
                        style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
                        data-testid="scoreboard-open-relay"
                      >
                        Open relay to log days &amp; close out →
                      </Link>
                    </div>
                  )}
                </div>
              ) : null;
              return (
                <div
                  className="grid grid-cols-7 sm:grid-cols-10 lg:grid-cols-[repeat(13,minmax(0,1fr))] gap-1.5 mb-4"
                  data-testid="scoreboard-grid"
                >
                  {weeks.map((w, i) => {
                    const locked = isSprint && w.relay_number > sprintWeeks;
                    const cell = locked ? (
                      <div
                        className="aspect-square rounded-lg flex items-center justify-center text-xs font-bold border border-dashed"
                        style={{ background: "var(--pr-bg-alt)", borderColor: "var(--pr-card-line)", color: "var(--pr-muted)" }}
                        title={`Relay ${w.relay_number} · unlocks with your Season Pass`}
                        data-testid={`scoreboard-cell-${w.relay_number}`}
                      >
                        🔒
                      </div>
                    ) : (
                      <button
                        onClick={() => setSelected(selected?.relay_number === w.relay_number ? null : w)}
                        className="relative aspect-square w-full rounded-lg flex items-center justify-center text-xs font-bold transition-transform hover:scale-110"
                        style={{
                          ...cellStyle(w),
                          outline: selected?.relay_number === w.relay_number ? `2px solid ${COLOR.ink}` : "none",
                          outlineOffset: 2,
                        }}
                        title={`Relay ${w.relay_number} · ${shortDate(w.week_start)} – ${shortDate(w.week_end)}${w.needs_closeout ? " · ready to close out" : ""}`}
                        data-testid={`scoreboard-cell-${w.relay_number}`}
                      >
                        {w.relay_number}
                        {w.needs_closeout && (
                          <span
                            className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full"
                            style={{ background: COLOR.gold, boxShadow: "0 0 0 1.5px var(--pr-bg)" }}
                            data-testid={`scoreboard-closeout-dot-${w.relay_number}`}
                            aria-label="Ready to close out"
                          />
                        )}
                      </button>
                    );
                    return (
                      <Fragment key={w.relay_number}>
                        {cell}
                        {i === rowEndIndex ? detailPanel : null}
                      </Fragment>
                    );
                  })}
                </div>
              );
            })()}

            <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] mb-5" style={{ color: COLOR.ink2 }} data-testid="scoreboard-legend">
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: "var(--pr-green-surface)" }} /> In the green</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: "var(--pr-ink-surface)" }} /> Cleared</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: "var(--pr-tint-red)" }} /> Ended short</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: "var(--pr-teal-surface)" }} /> PTO week</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: "var(--pr-tint-gold)", border: `2px solid ${COLOR.gold}` }} /> This week</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: "var(--pr-bg-alt)" }} /> Up ahead</span>
              <span className="inline-flex items-center gap-1.5"><span className="relative w-3 h-3 rounded" style={{ background: "var(--pr-ink-surface)" }}><span className="absolute top-0 right-0 w-1.5 h-1.5 rounded-full" style={{ background: COLOR.gold }} /></span> Paid · ready to close out</span>
            </div>

            {taxItems.length > 0 && (
              <div className="mt-6" data-testid="scoreboard-tax">
                <div className="rounded-xl px-4 py-3 mb-3 pr-card-dark" style={{ background: "var(--pr-ink-surface)" }}>
                  <p className="text-sm font-bold tracking-[0.2em]" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.gold }}>
                    QUARTERLY TAX DAYS
                  </p>
                </div>
                <div className="rounded-2xl border overflow-hidden" style={{ background: "var(--pr-card)", borderColor: COLOR.line }}>
                  {taxItems.map((it, i) => (
                    <label
                      key={it.quarter}
                      className="flex items-center gap-3 px-4 py-3.5 cursor-pointer"
                      style={{ borderTop: i > 0 ? `1px solid ${COLOR.line}` : "none" }}
                      data-testid={`tax-row-${it.quarter}`}
                    >
                      <input
                        type="checkbox"
                        checked={!!it.paid}
                        onChange={() => toggleTax(it)}
                        className="w-5 h-5 cursor-pointer"
                        style={{ accentColor: COLOR.green }}
                        data-testid={`tax-paid-${it.quarter}`}
                      />
                      <div className="flex-1">
                        <p className="text-sm font-bold" style={{ color: COLOR.ink }}>
                          {it.quarter}{it.due ? `, due ${it.due}` : ""}
                        </p>
                        {it.covers && <p className="text-[11px]" style={{ color: COLOR.muted }}>covers {it.covers}</p>}
                      </div>
                      <span
                        className="text-xs font-bold"
                        style={{ color: it.paid ? COLOR.green : COLOR.muted }}
                        data-testid={`tax-status-${it.quarter}`}
                      >
                        {it.paid ? "Paid ✓" : "Unpaid"}
                      </span>
                    </label>
                  ))}
                </div>
                <p className="text-[11px] mt-2" style={{ color: COLOR.muted }}>
                  {data.tax_checklist?.source === "sheet"
                    ? "Synced both ways with the Paid? boxes on your sheet's Scoreboard tab."
                    : "Saved in the app, connect your sheet and these tick the Paid? boxes there too."}
                </p>
              </div>
            )}

            {data.time_off && ((data.time_off.pto_days || 0) + (data.time_off.sick_days || 0) + (data.time_off.days_off || 0) + (data.time_off.pto_weeks || 0)) > 0 && (
              <div className="mt-6" data-testid="scoreboard-timeoff">
                <div className="rounded-xl px-4 py-3 mb-3 pr-card-dark" style={{ background: "var(--pr-teal-surface)" }}>
                  <p className="text-sm font-bold tracking-[0.2em]" style={{ fontFamily: "'Fraunces', Georgia, serif", color: "#fff" }}>
                    TIME OFF
                  </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Stat label="PTO days" value={`🌴 ${data.time_off.pto_days || 0}`} color={COLOR.teal} />
                  <Stat label="Sick days" value={`🤒 ${data.time_off.sick_days || 0}`} />
                  <Stat label="Days off" value={`😌 ${data.time_off.days_off || 0}`} />
                  <Stat label="PTO weeks" value={`${data.time_off.pto_weeks || 0}`} color={COLOR.teal} />
                </div>
                {data.time_off.covered_total > 0 && (
                  <p className="text-xs mt-2.5" style={{ color: COLOR.teal }} data-testid="scoreboard-timeoff-covered">
                    {fmt(data.time_off.covered_total)} of paid time off covered from your PTO bank. You paid yourself to rest. 🏁
                  </p>
                )}
                <p className="text-[11px] mt-1.5" style={{ color: COLOR.muted }}>
                  Paid draws sync to your sheet, <Link to="/app/timeoff" className="underline" style={{ color: COLOR.ink }}>plan time off →</Link>
                </p>
              </div>
            )}

            <MyProgress progress={data.progress} source={data.progress_source} banks={data.banks} />

            {/* "Create my race reel", moved off Daily so the dashboard stays
                focused. Scoreboard is the natural home: reviewers already have
                the whole race in view here. */}
            {isSprint ? (
              <div className="mt-6">
                <LockedCard id="scoreboard-season-reel-locked" icon="🎬" title="Season Reel & Year-End Scorecard"
                  body="Turn your whole race into a shareable highlight reel, every relay cleared, every dollar you paid yourself to rest. Unlocks with the 52-week race." compact />
              </div>
            ) : (
              <div className="mt-6" data-testid="scoreboard-season-reel">
                <SeasonReel />
              </div>
            )}
          </>
        )}

        <p className="text-[11px] text-center leading-relaxed px-4 py-8" style={{ color: COLOR.muted }}>
          Payday Relay is an educational tool. Not financial, tax, or investment advice.{" "}
          <Link to="/terms" className="underline">Terms</Link>
        </p>
        <div className="h-14 lg:hidden" />
        </main>
      </div>
      <CaddyBottomNav active="scoreboard" sprint={isSprint} />
    </div>
  );
}
